import { useEffect, useRef } from 'react';
import { hasAuthSession } from '../utils/auth/authConfig';

const SESSION_EXPIRE_COOKIE = 'sessionExpireAt';
const CHECK_INTERVAL = 10000;

const getSessionExpireAt = (): number | null => {
  const match = document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(`${SESSION_EXPIRE_COOKIE}=`));

  if (!match) {
    return null;
  }

  const value = Number(match.split('=')[1]);
  return Number.isNaN(value) ? null : value;
};

export const useSessionTimer = (onExpired: (message?: string) => void) => {
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasExpiredRef = useRef(false);
  const missingCookieCountRef = useRef(0);

  useEffect(() => {
    if (window.location.pathname === '/signin') {
      return;
    }

    hasExpiredRef.current = false;
    missingCookieCountRef.current = 0;

    const triggerExpired = () => {
      hasExpiredRef.current = true;
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      onExpired('세션이 만료되었습니다. 다시 로그인해주세요.');
    };

    const checkExpiry = () => {
      if (hasExpiredRef.current) {
        return;
      }

      const expireAt = getSessionExpireAt();

      if (expireAt === null) {
        if (hasAuthSession()) {
          missingCookieCountRef.current += 1;
          // 세션이 만료되었습니다. 다시 로그인해주세요. 2번 이상 발생하면 세션 만료 처리
          if (missingCookieCountRef.current >= 2) {
            triggerExpired();
          }
        } else {
          missingCookieCountRef.current = 0;
        }
        return;
      }

      missingCookieCountRef.current = 0;

      if (Date.now() >= expireAt) {
        triggerExpired();
      }
    };

    checkExpiry();

    if (!hasExpiredRef.current) {
      timerRef.current = setInterval(checkExpiry, CHECK_INTERVAL);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [onExpired]);
};
